import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { PackageSearch } from "lucide-react"
import { useGetItemsQuery, useGetItemGroupsQuery, usePatchItemMutation } from "shared/redux/endpoints/procurementItems"
import { useGetActiveBudgetCategoriesQuery } from "shared/redux/endpoints/procurementV2"
import { useGetPurchaseSuppliersQuery } from "shared/redux/endpoints/procurementPurchases"
import { Button } from "../../ui"
import {
  FilterBar,
  DataTable,
  StatusPill,
  MoneyText,
  EmptyState,
  en,
} from "procurement/design"
import ClampedText from "../ClampedText"
import ItemQuickDrawer from "./ItemQuickDrawer"
import InlineEditCell from "./InlineEditCell"
import { RULES } from "../../fieldRules"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "../../orderSubmitFeedback"
import useProcurementFilters from "../../hooks/useProcurementFilters"

const STATUS_OPTIONS = [
  { value: "active", label: en.status.active },
  { value: "inactive", label: en.status.inactive },
]

const ItemsListView = () => {
  const navigate = useNavigate()
  const { filters, setFilters, resetFilters } = useProcurementFilters({
    search: "",
    categoryId: "",
    groupId: "",
    status: "",
    vendorId: "",
    refPrice: "",
  })
  const [drawerId, setDrawerId] = useState(null)

  const { data: categories } = useGetActiveBudgetCategoriesQuery({ isActive: true })
  const { data: groups } = useGetItemGroupsQuery({ categoryId: filters.categoryId || undefined })
  const { data: items, isFetching } = useGetItemsQuery({
    categoryId: filters.categoryId || undefined,
    itemGroupId: filters.groupId || undefined,
    search: filters.search || undefined,
    activeOnly: filters.status === "active" || undefined,
    defaultVendorId: filters.vendorId || undefined,
    isApplyReferencePrice: filters.refPrice === "1" ? true : undefined,
    // BE defaults to size=20; the table sorts/pages client-side, so load all.
    size: 10000,
  })
  const { data: suppliers } = useGetPurchaseSuppliersQuery()
  const supplierOptions = useMemo(
    () => (suppliers || []).map((s) => ({ value: String(s.id), label: s.name })),
    [suppliers]
  )
  const [patchItem] = usePatchItemMutation()

  const categoryOptions = useMemo(
    () => (categories || []).map((c) => ({ value: String(c.id), label: c.name })),
    [categories]
  )
  const groupOptions = useMemo(
    () => (groups || []).map((g) => ({ value: String(g.id), label: g.name || g.code })),
    [groups]
  )

  const rows = useMemo(() => {
    const list = items || []
    if (filters.status === "inactive") return list.filter((r) => r.isActive === false)
    return list
  }, [items, filters.status])

  const toggleActive = async (row, next) => patchField(row, "isActive", next)

  const patchField = async (row, key, next) => {
    try {
      await patchItem({ id: row.id, [key]: next }).unwrap()
      showToastSuccess(en.toast.itemUpdated)
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't update this item."))
    }
  }

  const columns = [
    {
      key: "itemCode",
      header: en.items.itemCode,
      sortable: true,
      render: (r) => (
        <span style={{ fontVariantNumeric: "tabular-nums", color: "var(--text-secondary)", fontWeight: 400 }}>
          <InlineEditCell value={r.itemCode} rule={RULES.item.itemCode} onSave={(v) => patchField(r, "itemCode", v)} />
        </span>
      ),
    },
    {
      key: "name",
      header: en.items.name,
      strong: true,
      sortable: true,
      render: (r) => (
        <ClampedText lines={[r.name || ""]} maxWidth={280}>
          <InlineEditCell value={r.name} width={240} rule={RULES.item.name} onSave={(v) => patchField(r, "name", v)} />
        </ClampedText>
      ),
    },
    {
      key: "group",
      header: en.items.groupCategory,
      render: (r) => (
        <span>
          <span style={{ fontWeight: 600 }}>{r.itemGroupName || "—"}</span>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{r.categoryName || "—"}</div>
        </span>
      ),
    },
    {
      key: "standardPrice",
      header: en.items.standardPrice,
      align: "right",
      sortable: true,
      value: (r) => r.standardPrice,
      render: (r) => (
        <span>
          <InlineEditCell
            type="number"
            align="right"
            value={r.standardPrice}
            display={<MoneyText value={r.standardPrice} />}
            onSave={(v) => patchField(r, "standardPrice", v)}
          />
          <span style={{ color: "var(--text-muted)", fontSize: 12 }}>
            {" / "}
            <InlineEditCell value={r.uom} width={70} rule={RULES.item.uom} onSave={(v) => patchField(r, "uom", v)} />
          </span>
        </span>
      ),
    },
    {
      key: "defaultVendorName",
      header: en.items.defaultVendor,
      render: (r) => r.defaultVendorName || "—",
    },
    {
      key: "isActive",
      header: en.items.active,
      render: (r) => (
        <button
          type="button"
          title={r.isActive ? "Click to set inactive" : "Click to set active"}
          style={{ background: "none", border: 0, padding: 0, cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation()
            toggleActive(r, !r.isActive)
          }}
        >
          <StatusPill status={r.isActive ? "active" : "inactive"} />
        </button>
      ),
    },
    {
      key: "mustHaveOrder",
      header: en.categories.allowPoWithoutOrder,
      render: (r) => (
        <span onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={!r.mustHaveOrder}
            onChange={(e) => patchField(r, "mustHaveOrder", !e.target.checked)}
            aria-label="Allow PO without Order"
          />
        </span>
      ),
    },
    {
      key: "mustHavePurchase",
      header: en.categories.allowPaymentWithoutPo,
      render: (r) => (
        <span onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={!r.mustHavePurchase}
            onChange={(e) => patchField(r, "mustHavePurchase", !e.target.checked)}
            aria-label="Allow Payment without PO"
          />
        </span>
      ),
    },
    {
      key: "isApplyReferencePrice",
      header: "Giá nguyên tắc",
      render: (r) => (
        <span onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={!!r.isApplyReferencePrice}
            onChange={(e) => patchField(r, "isApplyReferencePrice", e.target.checked)}
            aria-label="Áp dụng giá nguyên tắc"
          />
        </span>
      ),
    },
  ]

  const hasFilters =
    filters.search || filters.categoryId || filters.groupId || filters.status || filters.vendorId || filters.refPrice

  return (
    <>
      <FilterBar
        values={filters}
        onChange={setFilters}
        onReset={resetFilters}
        fields={[
          { key: "search", label: en.filter.search, placeholder: en.filter.searchItems },
          { key: "categoryId", label: en.filter.category, type: "select", options: categoryOptions, placeholder: en.filter.allCategories },
          { key: "groupId", label: en.filter.group, type: "select", options: groupOptions, placeholder: en.filter.allGroups },
          { key: "status", label: en.filter.status, type: "select", options: STATUS_OPTIONS },
          { key: "vendorId", label: "Supplier", type: "select", options: supplierOptions, placeholder: "All suppliers" },
          { key: "refPrice", label: "Chỉ item giá nguyên tắc", type: "checkbox" },
        ]}
      />
      <DataTable
        columns={columns}
        rows={rows}
        isLoading={isFetching}
        onRowClick={(r) => setDrawerId(r.id)}
        initialSort={{ key: "name", dir: "asc" }}
        empty={
          <EmptyState
            icon={<PackageSearch aria-hidden />}
            title={hasFilters ? en.empty.noResultsTitle : en.items.emptyItems}
            text={hasFilters ? en.empty.noResultsText : en.items.emptyItemsText}
            action={
              hasFilters ? (
                <Button variant="secondary" size="sm" onClick={resetFilters}>
                  {en.common.clearFilters}
                </Button>
              ) : (
                <Button variant="primary" onClick={() => navigate("/procurement/items/new")}>
                  {en.items.newItem}
                </Button>
              )
            }
          />
        }
      />
      <ItemQuickDrawer itemId={drawerId} open={!!drawerId} onClose={() => setDrawerId(null)} />
    </>
  )
}

export default ItemsListView
